/**
 * 导出为 PSD 的图层树 → PSD 输入转换（导出对话框补充，参照 Photoshop，
 * ADR-0014）。
 *
 * 只保证自己写出去的 PSD 能被 Photoshop 打开，不保证 round-trip 无损。
 * 逐图层种类的处理策略：
 *
 * - **组/栅格/文本图层**：结构原样映射（名称/可见性/不透明度/混合模式/
 *   嵌套）。栅格与文本图层的像素走 `compositeLayerSubset`（`composite.ts`
 *   已有函数，含该图层自己的混合/蒙版/效果——图层样式因此已经烧录进
 *   像素，PSD 里不再是可编辑样式），裁到图层自身的内容矩形。
 * - **形状/填充/调整/智能对象图层**：这四种没有像素图层那种"自身内容
 *   矩形"的统一表达（调整图层理论上覆盖全图），v1 统一按整文档尺寸
 *   栅格化——ponytail 简化点，牺牲一点文件体积换实现简单，日后要收紧到
 *   实际内容 bbox 再优化，不是这次目标。
 * - **竖排文本 / 多 run 混排样式**：ag-psd 的竖排支持不稳定（见 ADR），
 *   多 run 混排在这里退化成统一样式（取第一个 run），都记入
 *   `rasterizedLayers` 披露列表。
 * - **非 8 位 / 非 RGB 文档**：图层结构在这个前提下没有意义，整体摊平成
 *   一个背景图层导出，同样记入披露列表。
 *
 * `rasterizedLayers` 汇总所有被降级处理的图层，调用方（`app/export.ts`）
 * 拼进用户可见的导出提示，不做静默降级。
 */
import { compositeDocument, compositeLayerSubset, type CompositeResult } from './composite';
import { applyTo } from '../engine/transform/mat3';
import type { Mat3 } from '../engine/transform/mat3';
import type { BlendMode, Document, Layer, LayerKind, Rect } from '../document/types';
import type { TileStore } from '../engine/tile/store';
import type { PsdDocumentInput, PsdLayerInput, PsdTextInfo } from '../io/codecs/psd';

/** 项目 27 种 BlendMode → ag-psd 认的 PS 名字字符串（穷尽映射，没有兜底分支）。 */
const BLEND_MODE_TO_PSD: Readonly<Record<BlendMode, PsdLayerInput['blendMode']>> = {
  normal: 'normal',
  dissolve: 'dissolve',
  multiply: 'multiply',
  screen: 'screen',
  overlay: 'overlay',
  darken: 'darken',
  lighten: 'lighten',
  colorDodge: 'color dodge',
  colorBurn: 'color burn',
  hardLight: 'hard light',
  softLight: 'soft light',
  difference: 'difference',
  exclusion: 'exclusion',
  linearBurn: 'linear burn',
  linearDodge: 'linear dodge',
  vividLight: 'vivid light',
  linearLight: 'linear light',
  pinLight: 'pin light',
  hardMix: 'hard mix',
  subtract: 'subtract',
  divide: 'divide',
  hue: 'hue',
  saturation: 'saturation',
  color: 'color',
  luminosity: 'luminosity',
  darkerColor: 'darker color',
  lighterColor: 'lighter color',
};

const RASTERIZED_KIND_LABEL: Readonly<Record<'shape' | 'fill' | 'adjustment' | 'smartObject', string>> = {
  shape: '形状图层',
  fill: '填充图层',
  adjustment: '调整图层',
  smartObject: '智能对象',
};

interface PixelRect {
  readonly left: number;
  readonly top: number;
  readonly right: number;
  readonly bottom: number;
}

/** 把矩形钳到画布范围内，取整到像素——PSD 的图层定位矩形要求整数。 */
function clampRectToCanvas(rect: Rect, width: number, height: number): PixelRect {
  const left = Math.max(0, Math.min(width, Math.round(rect.x)));
  const top = Math.max(0, Math.min(height, Math.round(rect.y)));
  const right = Math.max(left, Math.min(width, Math.round(rect.x + rect.width)));
  const bottom = Math.max(top, Math.min(height, Math.round(rect.y + rect.height)));
  return { left, top, right, bottom };
}

/** 图层局部空间矩形经 transform 变换后，在文档空间里的轴对齐外接矩形。 */
function boundingBoxOfLocalRect(rect: Rect, transform: Mat3): Rect {
  const corners = [
    { x: rect.x, y: rect.y },
    { x: rect.x + rect.width, y: rect.y },
    { x: rect.x, y: rect.y + rect.height },
    { x: rect.x + rect.width, y: rect.y + rect.height },
  ].map((p) => applyTo(transform, p));
  const xs = corners.map((p) => p.x);
  const ys = corners.map((p) => p.y);
  const minX = Math.min(...xs);
  const minY = Math.min(...ys);
  return { x: minX, y: minY, width: Math.max(...xs) - minX, height: Math.max(...ys) - minY };
}

/** 从整文档尺寸的合成结果里裁出一块矩形的 RGBA8。 */
function cropPixels(full: CompositeResult, rect: PixelRect): { data: Uint8Array; width: number; height: number } {
  const w = Math.max(0, rect.right - rect.left);
  const h = Math.max(0, rect.bottom - rect.top);
  const out = new Uint8Array(w * h * 4);
  for (let row = 0; row < h; row++) {
    const srcStart = ((rect.top + row) * full.width + rect.left) * 4;
    out.set(full.pixels.subarray(srcStart, srcStart + w * 4), row * w * 4);
  }
  return { data: out, width: w, height: h };
}

/** `#rrggbb` → 0–255 三通道。跟本仓库其它几处（main.tsx/ColorPickerDialog.tsx）同样的小写字面量解析，没有共享成一个模块——三行代码不值得为它建一个新的公共工具文件。 */
function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const n = Number.parseInt(hex.replace('#', ''), 16);
  return { r: (n >> 16) & 0xff, g: (n >> 8) & 0xff, b: n & 0xff };
}

export interface BuildPsdResult {
  readonly input: PsdDocumentInput;
  /** 被降级/栅格化处理的图层说明，拼进导出提示用，不做静默降级。 */
  readonly rasterizedLayers: readonly string[];
}

/** 把文档图层树转换成 `encodePsdBytes` 要的输入。 */
export function buildPsdInput(document: Document, tiles: TileStore): BuildPsdResult {
  const { width, height } = document.size;
  const rasterizedLayers: string[] = [];

  // 非 8 位/RGB：图层结构在这个前提下没有意义，整体摊平成一张背景图层
  if (document.bitDepth !== 8 || document.colorMode !== 'rgb') {
    const flat = compositeDocument(document, tiles);
    rasterizedLayers.push('整份文档（非 8 位 RGB，已整体转换成 8 位 RGB 再导出）');
    return {
      input: {
        width,
        height,
        layers: [
          {
            name: document.meta.title.length > 0 ? document.meta.title : '背景',
            hidden: false,
            opacity: 1,
            blendMode: 'normal',
            left: 0,
            top: 0,
            right: width,
            bottom: height,
            pixels: { data: flat.pixels, width, height },
          },
        ],
      },
      rasterizedLayers,
    };
  }

  const rasterizedKindNote = (name: string, kind: LayerKind): void => {
    const label = RASTERIZED_KIND_LABEL[kind as keyof typeof RASTERIZED_KIND_LABEL];
    rasterizedLayers.push(`${name}（${label}，已栅格化）`);
  };

  function convertLayer(layer: Layer): PsdLayerInput {
    const hidden = !layer.visible;
    const opacity = layer.opacity;
    const blendMode = BLEND_MODE_TO_PSD[layer.blendMode];

    if (layer.kind === 'group') {
      return {
        name: layer.name,
        hidden,
        opacity,
        blendMode: layer.passThrough ? 'pass through' : blendMode,
        // 组图层没有像素，ag-psd 不看这四个字段定位——占满整个画布即可
        left: 0,
        top: 0,
        right: width,
        bottom: height,
        // 项目里数组末位在最上（图层面板渲染顺序），ag-psd 期望数组顺序
        // 就是从上到下——同一条约定在下面顶层 children 那里也要反一次
        children: [...layer.children].reverse().map(convertLayer),
        opened: !layer.collapsed,
      };
    }

    if (layer.kind === 'raster') {
      const rect = clampRectToCanvas(layer.bounds, width, height);
      const full = compositeLayerSubset(document, tiles, [layer.id]);
      const pixels = cropPixels(full, rect);
      if (layer.effects.some((e) => e.enabled)) {
        rasterizedLayers.push(`${layer.name}（含图层样式，已烧录进像素）`);
      }
      return { name: layer.name, hidden, opacity, blendMode, ...rect, pixels };
    }

    if (layer.kind === 'text') {
      const docRect = boundingBoxOfLocalRect(layer.box, layer.transform);
      const rect = clampRectToCanvas(docRect, width, height);
      const full = compositeLayerSubset(document, tiles, [layer.id]);
      const pixels = cropPixels(full, rect);
      const first = layer.runs[0];
      const text: PsdTextInfo = {
        text: layer.runs.map((run) => run.text).join(''),
        vertical: layer.writingMode === 'vertical',
        ...(first === undefined
          ? {}
          : {
              fontFamily: first.fontFamily,
              fontSize: first.fontSize,
              ...(first.fill.type === 'solid' ? { color: hexToRgb(first.fill.color) } : {}),
            }),
      };
      if (layer.writingMode === 'vertical') {
        rasterizedLayers.push(`${layer.name}（竖排文本，只保留像素预览，不保证可编辑）`);
      }
      if (layer.runs.length > 1) {
        rasterizedLayers.push(`${layer.name}（多段文本混排样式已合并成统一样式）`);
      }
      return { name: layer.name, hidden, opacity, blendMode, ...rect, pixels, text };
    }

    // shape / fill / adjustment / smartObject：见文件头，v1 统一栅格化成
    // 整文档尺寸的普通图层
    rasterizedKindNote(layer.name, layer.kind);
    const full = compositeLayerSubset(document, tiles, [layer.id]);
    return {
      name: layer.name,
      hidden,
      opacity,
      blendMode,
      left: 0,
      top: 0,
      right: width,
      bottom: height,
      pixels: { data: full.pixels, width, height },
    };
  }

  const layers = [...document.root.children].reverse().map(convertLayer);
  return { input: { width, height, layers }, rasterizedLayers };
}
